import React, { useState, useEffect } from 'react';
import { 
  Box, 
  TextField, 
  Button, 
  Typography, 
  Grid, 
  Card, 
  CardContent, 
  CardMedia, 
  CardActions,
  CircularProgress,
  Alert
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import SearchIcon from '@mui/icons-material/Search';
import axiosInstance from '../../axiosInstance';

/**
 * Component for searching and adding movies to collection
 * @param {Object} props - Component props
 * @param {function} props.onAddMovie - Function to handle adding a movie
 * @param {boolean} props.isWishlist - Whether this is a wishlist
 * @param {string} props.initialQuery - Initial search query
 * @param {boolean} props.hideSearchBox - Whether to hide the search box
 * @param {string} props.itemType - Type of item ('movie')
 * @returns {JSX.Element} Movie search component
 */
const MovieSearch = ({ onAddMovie, isWishlist = false, initialQuery = '', hideSearchBox = false, itemType = 'movie' }) => {
  const [searchQuery, setSearchQuery] = useState(initialQuery);
  const [searchResults, setSearchResults] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  
  // If initialQuery is provided, perform search automatically
  useEffect(() => {
    if (initialQuery) {
      setSearchQuery(initialQuery);
      // Use a timeout to ensure the component is fully mounted
      const timer = setTimeout(() => {
        handleSearch({ preventDefault: () => {} });
      }, 100);
      return () => clearTimeout(timer);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [initialQuery]);

  /**
   * Handle search for movies using OMDB API
   */
  const handleSearch = async (e) => {
    e.preventDefault();
    if (!searchQuery.trim()) return;

    setLoading(true);
    setError('');
    setSearchResults([]);
    
    console.log('Searching for movies with query:', searchQuery);
    
    try {
      // Make sure we're using the correct endpoint path
      // If axiosInstance already has baseURL set to '/api', we shouldn't include '/api' again
      const response = await axiosInstance.get(`/movies/search?query=${encodeURIComponent(searchQuery)}`);
      console.log('Movie search response:', response);
      
      if (response.data && Array.isArray(response.data)) {
        setSearchResults(response.data);
        if (response.data.length === 0) {
          setError('No movies found. Try a different search term.');
        }
      } else if (response.data && response.data.Error) {
        console.error('OMDB API error:', response.data.Error);
        setError(`Error from movie database: ${response.data.Error}`);
      } else {
        console.error('Unexpected response format:', response.data);
        setError('Unexpected response format from the server');
      }
    } catch (err) {
      console.error('Error searching for movies:', err);
      if (err.response?.status === 500 && err.response?.data?.error?.includes('OMDB API key')) {
        setError('The movie search service is not properly configured. Please contact an administrator.');
      } else {
        setError(err.response?.data?.error || 'Error searching for movies. Please try again.');
      }
    } finally {
      setLoading(false);
    }
  };

  /**
   * Handle adding a movie to collection or wishlist
   */
  const handleAddMovie = async (movie) => {
    // Create a movie object with necessary fields
    const movieToAdd = {
      imdbId: movie.imdbID, // Note: using imdbId (lowercase d) to match the Movie model
      name: movie.Title,
      year: movie.Year,
      director: movie.Director,
      genre: movie.Genre,
      runtime: movie.Runtime,
      plot: movie.Plot,
      imageUrl: movie.Poster !== 'N/A' ? movie.Poster : '',
      format: 'DVD', // Using a valid format from the Movie model enum
      condition: 'Good / Acceptable', // Using a valid condition from the Movie model enum
      edition: 'Standard Edition',
      priority: isWishlist ? 'Medium' : undefined,
      watched: false
    };

    console.log(`Adding ${itemType} item with data:`, movieToAdd);
    console.log('ItemType being used:', itemType);
    onAddMovie(movieToAdd);
  };

  return (
    <Box sx={{ mt: 4 }}>
      {!hideSearchBox && (
        <>
          <Typography variant="h5" gutterBottom>
            Find New Movies
          </Typography>
          
          <Box component="form" onSubmit={handleSearch} sx={{ display: 'flex', mb: 3 }}>
            <TextField
              fullWidth
              label="Search for movies by title"
              variant="outlined"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              sx={{ mr: 1 }}
            />
            <Button 
              type="submit" 
              variant="contained" 
              startIcon={<SearchIcon />}
              disabled={loading}
            >
              Search
            </Button>
          </Box>
        </>
      )}

      {loading && (
        <Box sx={{ display: 'flex', justifyContent: 'center', my: 4 }}>
          <CircularProgress />
        </Box>
      )}

      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}

      {searchResults.length > 0 && (
        <Box sx={{ mt: 2 }}>
          <Typography variant="h6" gutterBottom>
            Search Results
          </Typography>
          <Grid container spacing={3}>
            {searchResults.map((movie) => (
              <Grid item xs={12} sm={6} md={4} key={movie.imdbID}>
                <Card sx={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
                  {movie.Poster && movie.Poster !== 'N/A' ? (
                    <CardMedia
                      component="img"
                      height="200"
                      image={movie.Poster}
                      alt={movie.Title}
                      sx={{ objectFit: 'contain', pt: 2 }}
                    />
                  ) : (
                    <Box sx={{ height: 200, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                      <Typography variant="body2" color="text.secondary">
                        No image available
                      </Typography>
                    </Box>
                  )}
                  <CardContent sx={{ flexGrow: 1 }}>
                    <Typography gutterBottom variant="h6" component="div">
                      {movie.Title}
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      Year: {movie.Year || 'N/A'}
                    </Typography>
                    {movie.Director && (
                      <Typography variant="body2" color="text.secondary">
                        Director: {movie.Director}
                      </Typography>
                    )}
                    {movie.Genre && (
                      <Typography variant="body2" color="text.secondary">
                        Genre: {movie.Genre}
                      </Typography>
                    )}
                  </CardContent>
                  <CardActions>
                    <Button 
                      size="small" 
                      startIcon={<AddIcon />}
                      onClick={() => handleAddMovie(movie)}
                    >
                      Add to {isWishlist ? 'Wishlist' : 'Collection'}
                    </Button>
                  </CardActions>
                </Card>
              </Grid>
            ))}
          </Grid>
        </Box>
      )}
    </Box>
  );
};

export default MovieSearch;
